import { IconName } from 'react-frontier';

export interface APIResponse<T=any>{
	error: boolean,
	code: string,
	message: string,
	data?: T & { [x: string]: any },
}

export enum MetadataType{
	INPUT = 1,
	TEXTAREA = 2,
	DROPDOWN = 20,
	MULTIPLE = 21,
	CHECKBOX = 40,
}

export interface PromoterFormMetadata{
	metadata_id: number,
	metadata_name: string,
	metadata_comment: string,
	type_id: MetadataType,
	value: string,
	value_id: number,
}

export interface MetadataItem{
	metadata_id: number,
	metadata_name: string,
	metadata_comment: string,
	type_id: MetadataType,
	sort_order: number,
	deleted?: boolean,
}

export enum SurveyQuestionType{
	INPUT = 1,
	TEXTAREA = 2,
	DROPDOWN = 10,
	MULTIPLE = 11,
	YESNO = 12,
	RATING = 13,
	DATE = 50,
	STATIC = 90,
}

export interface SurveyQuestionValue{
	value_id: number,
	question_id?: number,
	value: string,
	sort_order?: number,
}

export interface SurveyQuestionItem{
	question_id: number,
	type_id: SurveyQuestionType,
	question: string,
	question_comment?: string,
	default_value?: string,
	sort_order: number,
	required: boolean,
	deleted?: boolean,
	values?: SurveyQuestionValue[],
}

export interface SurveyFormQuestion{
	question_id: number,
	type_id: SurveyQuestionType,
	question: string,
	question_comment?: string,
	default_value?: string,
	required: boolean,
	values?: SurveyQuestionValue[],
}

export interface SurveyAnswerItem{
	answer_id: number,
	question_id: number,
	question: string,
	type_id: SurveyQuestionType,
	value_id?: number,
	answer: string,
}

export interface SurveyResponseItem{
	response_id: number,
	promoter_id: number,
	promoter_name?: string,
	user_assigned?: number,
	user_assigned_name?: string,
	answer_count?: number,
	date_created: number,
	answers?: SurveyAnswerItem[],
}

export enum ProspectSource{
	WEB = 1,
	USER = 2,
}

export interface ProspectListItem{
	prospect_id: number,
	prospect_name: string,
	status_id: number,
	status_name: string,
	user_assigned?: number,
	user_assigned_name?: string,
	source_id?: ProspectSource,
	source_name?: string,
	last_activity?: string,
	date_created: number,
}

export interface PromoterListItem{
	promoter_id: number,
	promoter_name: string,
	status_id: number,
	status_name: string,
	user_assigned?: number,
	user_assigned_name?: string,
	last_activity?: string,
	date_created: number,
}

export interface PromoterStatus{
	status_id: number,
	status_name: string,
}

export interface UserItem{
	user_id: number,
	first_name: string,
	last_name: string,
	name: string,
	email: string,
}

export interface UserClass{
	user_id: number,
	first_name: string,
	last_name: string,
	email: string,
	name?: string,
	superadmin?: boolean,
	active?: boolean,
	date_created: number,
	access?: number[],
}

export interface PromoterComment{
	comment_id: number,
	prospect_id?: number,
	promoter_id?: number,
	user_id: number,
	user_creator_name: string,
	content: string,
	date_created: number,
	sending?: boolean,
	local_id?: number,
}

export interface PromoterCondition{
	note_id: number,
	promoter_id: number,
	title: string,
	description?: string,
	user_creator: number,
	user_creator_name: string,
	date_created: number,
}

export interface PromoterActivity{
	activity_id: number,
	prospect_id?: number,
	promoter_id?: number,
	activity_type_id: number,
	type_name: string,
	title: string,
	description?: string,
	outcome?: string,
	date_activity: number,
	completed: number,
	date_completed?: number,
	user_creator: number,
	user_creator_name: string,
	target_name?: string,
	date_created: number,
}

export interface PromoterActivityType{
	activity_type_id: number,
	type_name: string,
	deleted?: boolean,
}

export enum AssetType{
	IMAGE = 1,
	PDF = 2,
}

export interface Asset{
	asset_id: number,
	type_id: AssetType,
	asset_name: string,
	description?: string,
	user_creator: number,
	user_creator_name?: string,
	date_created: number,
	prospect_id?: number,
	url?: string,
	download_url?: string,
}

export interface EventItem{
	date_id: number,
	event_id: number,
	event_name: string,
	category_name?: string,
	date: number,
	venue_name?: string,
	city?: string,
	tickets_sold: number,
	tickets_free: number,
	poster_url?: string,
}

export interface ExternalEvent{
	external_id: number,
	event_name: string,
	venue_name: string,
	date: number,
	category_id: number | null,
	category_name: string | null,
	company_id: number | null,
	company_name: string | null,
	promoter_id: number | null,
	promoter_name: string | null,
	seller_id: number | null,
	seller_name: string | null,
	city: string | null,
	state: string | null,
	capacity: number | null,
	exclusive: number,
}

export interface ExternalUploadResult{
	inserted: number,
	updated: number,
	companies_created: number,
	promoters_linked: number,
	promoters_unmatched: string[],
	sellers_linked: number,
	sellers_unmatched: string[],
	errors: { row: number, reason: string }[],
}

export type SetLoading = (l: boolean)=>void;

export interface Breadcrumb{
	text: string,
	url: string,
	iconName?: IconName,
	sidebar?: JSX.Element,
}

export interface SiteRoute{
	path: string,
	title: string | ((params: { [x: string]: string })=>string),
	iconName?: IconName,
	ignore?: boolean,
	sidebar?: JSX.Element,
	children?: SiteRoute[],
}

export {}